import { z } from 'zod';
import { slugSchema } from './catalog.js';

export const articleStatusSchema = z.enum(['DRAFT', 'PUBLISHED', 'ARCHIVED']);

export const articleListQuerySchema = z.object({
  tag: slugSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(12),
});

export const articleParamsSchema = z.object({
  slug: slugSchema,
});

export const articleTagInputSchema = z.object({
  slug: slugSchema,
  name: z.string().min(1).max(60),
});

/**
 * Field mentah tanpa nilai bawaan.
 *
 * Penting: .partial() TIDAK membatalkan .default(). Kalau schema ubah dibuat
 * dari schema buat yang punya default, setiap PATCH parsial akan diam-diam
 * mengisi field yang tidak dikirim dengan nilai bawaannya — artikel terbit
 * turun jadi draf dan tagnya terhapus.
 */
const articleFields = {
  title: z.string().min(3, 'Judul minimal 3 karakter').max(180),
  excerpt: z.string().max(400).optional(),

  /** Keluaran editor, dipakai saat artikel dibuka lagi untuk disunting. */
  contentJson: z.record(z.string(), z.unknown()),

  /** HTML dari editor; server tetap menyaringnya sebelum disimpan. */
  contentHtml: z.string().min(1).max(400_000),

  status: articleStatusSchema,
  seoTitle: z.string().max(180).optional(),
  seoDescription: z.string().max(320).optional(),
  coverMediaId: z.string().max(64).optional(),
  tags: z.array(articleTagInputSchema).max(8),
};

export const articleInputSchema = z.object({
  slug: slugSchema,
  ...articleFields,
  contentJson: articleFields.contentJson.default({}),
  status: articleFields.status.default('DRAFT'),
  tags: articleFields.tags.default([]),
});

export const articleUpdateSchema = z
  .object(articleFields)
  .partial()
  .refine((data) => Object.keys(data).length > 0, {
    message: 'Tidak ada perubahan yang dikirim',
  });

export const adminArticleQuerySchema = z.object({
  status: articleStatusSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});

export type ArticleStatus = z.infer<typeof articleStatusSchema>;
export type ArticleListQuery = z.infer<typeof articleListQuerySchema>;
export type ArticleParams = z.infer<typeof articleParamsSchema>;
export type ArticleInput = z.infer<typeof articleInputSchema>;
export type ArticleUpdate = z.infer<typeof articleUpdateSchema>;
export type AdminArticleQuery = z.infer<typeof adminArticleQuerySchema>;
